import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, relTime } from '../lib/api'

/**
 * Marketing-site content management: blog posts, community forum moderation, and tags.
 * SuperAdmin only. Everything here is CRUD over the /content/admin API — create, edit, publish,
 * approve, pin, lock, and soft-delete. It never touches the sending system.
 */
type Tab = 'blog' | 'community' | 'tags'

export function ContentAdmin() {
  const [tab, setTab] = useState<Tab>('blog')
  return (
    <div>
      <h1>Content</h1>
      <p className="lede">Manage the marketing site — blog posts, community threads, and tags.</p>
      <div className="segmented" style={{ marginBottom: 18 }}>
        <button className={tab === 'blog' ? 'active' : ''} onClick={() => setTab('blog')}>Blog</button>
        <button className={tab === 'community' ? 'active' : ''} onClick={() => setTab('community')}>Community</button>
        <button className={tab === 'tags' ? 'active' : ''} onClick={() => setTab('tags')}>Tags</button>
      </div>
      {tab === 'blog' && <BlogAdmin />}
      {tab === 'community' && <CommunityAdmin />}
      {tab === 'tags' && <TagsAdmin />}
    </div>
  )
}

// ------------------------------------------------------------------ Blog
const EMPTY_BLOG = { id: '', title: '', slug: '', excerpt: '', body_html: '', category: 'General', author: 'ItsBingMyBusiness', tags: '', read_minutes: 3, is_published: true }

const SITE = 'https://itsbingmybusiness.com'

function BlogAdmin() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['content', 'blog'], queryFn: api.content.blogList })
  const [form, setForm] = useState<any>(EMPTY_BLOG)
  const [editing, setEditing] = useState(false)
  const [viewId, setViewId] = useState<string | null>(null)

  const reset = () => { setForm(EMPTY_BLOG); setEditing(false) }
  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, tags: form.tags ? String(form.tags).split(',').map((t: string) => t.trim()).filter(Boolean) : [] }
      return editing ? api.content.blogUpdate(form.id, body) : api.content.blogCreate(body)
    },
    onSuccess: () => { reset(); qc.invalidateQueries({ queryKey: ['content', 'blog'] }) },
  })
  const del = useMutation({
    mutationFn: (id: string) => api.content.blogDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content', 'blog'] }),
  })

  const posts = data?.data ?? []
  return (
    <div className="dash-cols">
      <div className="dash-col">
        <div className="card">
          <h2 style={{ marginTop: 0 }}>{editing ? 'Edit post' : 'New post'}</h2>
          <div className="field"><label>Title</label><input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
          <div className="field"><label>Slug <span className="muted">(auto if blank)</span></label><input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="auto-from-title" /></div>
          <div className="field"><label>Excerpt</label><input value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} /></div>
          <div className="field"><label>Body (HTML)</label><textarea rows={8} value={form.body_html} onChange={e => setForm({ ...form, body_html: e.target.value })} style={{ fontFamily: 'monospace', fontSize: 13 }} /></div>
          <div className="row" style={{ gap: 10 }}>
            <div className="field" style={{ flex: 1 }}><label>Category</label><input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></div>
            <div className="field" style={{ flex: 1 }}><label>Author</label><input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} /></div>
          </div>
          <div className="field"><label>Tags <span className="muted">(comma-separated)</span></label><input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="deliverability, dkim" /></div>
          <label className="row" style={{ gap: 8, cursor: 'pointer', margin: '4px 0 12px' }}>
            <input type="checkbox" checked={form.is_published} onChange={e => setForm({ ...form, is_published: e.target.checked })} /> Published
          </label>
          <div className="row" style={{ gap: 8 }}>
            <button disabled={!form.title.trim() || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create post'}</button>
            {editing && <button className="ghost" onClick={reset}>Cancel</button>}
          </div>
          {save.isError && <div className="alert error" style={{ marginTop: 10 }}>{(save.error as any)?.message ?? 'Failed.'}</div>}
        </div>
      </div>
      <div className="dash-col">
        <div className="card flush">
          {isLoading && <div className="skeleton" style={{ height: 200 }} />}
          {!isLoading && posts.length === 0 && <div className="empty" style={{ border: 0 }}><p><strong>No posts yet</strong></p></div>}
          {posts.map((p: any) => (
            <div key={p.id} className="hook-row">
              <div className="spread">
                <div style={{ minWidth: 0 }}>
                  <div className="name truncate">{p.title}</div>
                  <div className="small muted">{p.category} · {p.is_published ? 'Published' : 'Draft'} · {relTime(p.updated_at)}</div>
                </div>
                <div className="row" style={{ gap: 6 }}>
                  <button className="secondary small" onClick={() => setViewId(viewId === p.id ? null : p.id)}>{viewId === p.id ? 'Hide' : 'View'}</button>
                  <button className="secondary small" onClick={() => { setForm({ ...p, tags: (p.tags ?? []).join(', ') }); setEditing(true); window.scrollTo({ top: 0, behavior: 'smooth' }) }}>Edit</button>
                  <button className="ghost danger small" onClick={() => confirm(`Delete "${p.title}"?`) && del.mutate(p.id)}>Delete</button>
                </div>
              </div>
              {viewId === p.id && (
                <div className="card" style={{ marginTop: 10, background: 'var(--surface-2, rgba(0,0,0,.02))' }}>
                  <div className="small muted" style={{ marginBottom: 8 }}>
                    {p.category} · {p.read_minutes} min · by {p.author}
                    {(p.tags ?? []).length > 0 && <> · {(p.tags).join(', ')}</>}
                    {' · '}<a className="linklike" href={`${SITE}/blog/${p.slug}/`} target="_blank" rel="noopener">View on site ↗</a>
                  </div>
                  {p.excerpt && <p style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>{p.excerpt}</p>}
                  <div className="blog-preview" dangerouslySetInnerHTML={{ __html: p.body_html }} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ------------------------------------------------------------------ Community
function CommunityAdmin() {
  const qc = useQueryClient()
  const [pending, setPending] = useState(false)
  const { data, isLoading } = useQuery({ queryKey: ['content', 'threads', pending], queryFn: () => api.content.threadList(pending) })
  const [openSlug, setOpenSlug] = useState<string | null>(null)

  const upd = useMutation({
    mutationFn: (v: { id: string; body: any }) => api.content.threadUpdate(v.id, v.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content', 'threads'] }),
  })
  const del = useMutation({
    mutationFn: (id: string) => api.content.threadDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content', 'threads'] }),
  })
  const threads = data?.data ?? []

  return (
    <div>
      <div className="segmented" style={{ marginBottom: 14 }}>
        <button className={!pending ? 'active' : ''} onClick={() => setPending(false)}>All</button>
        <button className={pending ? 'active' : ''} onClick={() => setPending(true)}>Pending review</button>
      </div>
      {isLoading && <div className="skeleton" style={{ height: 200 }} />}
      {!isLoading && threads.length === 0 && <div className="empty"><p><strong>{pending ? 'Nothing awaiting review' : 'No threads yet'}</strong></p></div>}
      <div className="card flush">
        {threads.map((t: any) => (
          <div key={t.id} className="hook-row">
            <div className="spread" style={{ alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0 }}>
                <div className="name truncate">{t.is_pinned && '📌 '}{t.title}</div>
                <div className="small muted">{t.author_name} · {t.category} · {t.reply_count} replies · {t.likes} likes · {relTime(t.created_at)}</div>
                <div className="row" style={{ gap: 6, marginTop: 6 }}>
                  <span className={`badge ${t.is_approved ? 'ok' : 'pending'}`}>{t.is_approved ? 'Approved' : 'Pending'}</span>
                  {t.is_locked && <span className="badge neutral">Locked</span>}
                </div>
              </div>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button className="secondary small" onClick={() => upd.mutate({ id: t.id, body: { isApproved: !t.is_approved } })}>{t.is_approved ? 'Unapprove' : 'Approve'}</button>
                <button className="secondary small" onClick={() => upd.mutate({ id: t.id, body: { isPinned: !t.is_pinned } })}>{t.is_pinned ? 'Unpin' : 'Pin'}</button>
                <button className="secondary small" onClick={() => upd.mutate({ id: t.id, body: { isLocked: !t.is_locked } })}>{t.is_locked ? 'Unlock' : 'Lock'}</button>
                <button className="secondary small" onClick={() => setOpenSlug(openSlug === t.slug ? null : t.slug)}>{openSlug === t.slug ? 'Hide' : 'View'}</button>
                <button className="ghost danger small" onClick={() => confirm(`Delete "${t.title}"?`) && del.mutate(t.id)}>Delete</button>
              </div>
            </div>
            {openSlug === t.slug && <ThreadReplies slug={t.slug} />}
          </div>
        ))}
      </div>
    </div>
  )
}

function ThreadReplies({ slug }: { slug: string }) {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['content', 'thread', slug], queryFn: () => api.content.thread(slug) })
  const delReply = useMutation({
    mutationFn: (id: string) => api.content.replyDelete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['content', 'thread', slug] }); qc.invalidateQueries({ queryKey: ['content', 'threads'] }) },
  })
  const replies = data?.replies ?? []
  const thread = data?.thread
  return (
    <div style={{ marginTop: 10, paddingLeft: 12, borderLeft: '2px solid var(--line)' }}>
      {thread && (
        <div className="card" style={{ marginBottom: 10, background: 'var(--surface-2, rgba(0,0,0,.02))' }}>
          <div className="small muted" style={{ marginBottom: 6 }}>
            Original post · {thread.author_name}
            {' · '}<a className="linklike" href={`https://itsbingmybusiness.com/community/${slug}/`} target="_blank" rel="noopener">View on site ↗</a>
          </div>
          <div dangerouslySetInnerHTML={{ __html: thread.body_html }} />
        </div>
      )}
      <div className="small muted" style={{ marginBottom: 4 }}>Replies</div>
      {replies.length === 0 && <div className="small muted">No approved replies.</div>}
      {replies.map((r: any) => (
        <div key={r.id} className="spread" style={{ padding: '6px 0', alignItems: 'flex-start' }}>
          <div className="small" style={{ minWidth: 0 }}><strong>{r.author_name}</strong> <span className="muted">· {relTime(r.created_at)}</span><div dangerouslySetInnerHTML={{ __html: r.body_html }} /></div>
          <button className="ghost danger small" onClick={() => delReply.mutate(r.id)}>Delete</button>
        </div>
      ))}
    </div>
  )
}

// ------------------------------------------------------------------ Tags
function TagsAdmin() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['content', 'tags'], queryFn: api.content.tagList })
  const [name, setName] = useState('')
  const [scope, setScope] = useState('both')
  const create = useMutation({
    mutationFn: () => api.content.tagCreate({ name, scope }),
    onSuccess: () => { setName(''); qc.invalidateQueries({ queryKey: ['content', 'tags'] }) },
  })
  const del = useMutation({
    mutationFn: (id: string) => api.content.tagDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content', 'tags'] }),
  })
  const tags = data?.data ?? []
  return (
    <div className="card">
      <div className="row" style={{ gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input placeholder="Tag name" value={name} onChange={e => setName(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
        <select value={scope} onChange={e => setScope(e.target.value)}><option value="both">Both</option><option value="blog">Blog</option><option value="community">Community</option></select>
        <button disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>Add tag</button>
      </div>
      <div className="chips">
        {tags.map((t: any) => (
          <span key={t.id} className="chip" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {t.name} <span className="muted">({t.scope})</span>
            <button className="linklike" style={{ color: 'var(--danger)' }} onClick={() => del.mutate(t.id)}>×</button>
          </span>
        ))}
        {tags.length === 0 && <span className="muted small">No tags yet.</span>}
      </div>
    </div>
  )
}
